/**
 * Shared form submission handler for Cloudflare Worker + Turnstile.
 *
 * Store-agnostic utility consumed by all form sections via Alpine.js.
 * Reads configuration from window.GJ (set in theme.liquid).
 *
 * Usage in a section's x-data:
 *   FormHandler.create({
 *     formType: 'contact',
 *     fields: { name: '', email: '', message: '' },
 *     requiredFields: ['email'],
 *     hasFiles: false,
 *     onSuccess: () => { ... }
 *   })
 */
window.FormHandler = {
  /** Maximum total upload size in bytes (35 MB). */
  MAX_UPLOAD_BYTES: 36700160,

  /** Allowed file extensions for upload validation. */
  ALLOWED_EXTENSIONS: ['.pdf', '.dwg', '.png', '.jpg', '.jpeg', '.xls', '.xlsx'],

  /**
   * Create a form handler instance for Alpine.js x-data.
   *
   * @param {object}            config
   * @param {string}            config.formType       — form_type value sent to the Worker
   * @param {object}            config.fields         — initial field values { name: '', email: '', ... }
   * @param {string[]}          [config.requiredFields] — field keys that must be non-empty
   * @param {boolean}           [config.hasFiles]     — whether this form supports file uploads
   * @param {string[]}          [config.allowedExtensions] — override allowed file extensions
   * @param {Function}          [config.onSuccess]    — callback after successful submission
   * @returns {object} Alpine.js component data
   */
  create(config) {
    const {
      formType,
      fields,
      requiredFields = [],
      hasFiles = false,
      allowedExtensions = null,
      onSuccess = null,
    } = config;

    const extensions = allowedExtensions || window.FormHandler.ALLOWED_EXTENSIONS;

    return {
      busy: false,
      success: false,
      error: '',
      refNumber: '',
      form: { ...fields },

      // File handling (only used when hasFiles is true)
      files: [],
      fileObjects: [],

      /**
       * Reset the form to its initial state for another submission.
       */
      reset() {
        this.busy = false;
        this.success = false;
        this.error = '';
        this.refNumber = '';
        this.form = { ...fields };
        this.files = [];
        this.fileObjects = [];
      },

      /**
       * Add files from an input or drag-and-drop event.
       * Validates extension and accumulates File objects.
       *
       * @param {FileList} fileList
       */
      addFiles(fileList) {
        if (!hasFiles) return;

        const allowed = extensions;

        Array.from(fileList).forEach((file) => {
          const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
          if (!allowed.includes(ext)) {
            this.error = `File type not allowed: ${file.name}`;
            return;
          }
          this.files.push(file.name);
          this.fileObjects.push(file);
        });
      },

      /**
       * Remove a file by index.
       *
       * @param {number} index
       */
      removeFile(index) {
        this.files.splice(index, 1);
        this.fileObjects.splice(index, 1);
      },

      /**
       * Submit the form to the Cloudflare Worker.
       *
       * Flow:
       * 1. Client-side required field validation
       * 2. File size validation (if applicable)
       * 3. Acquire Turnstile token
       * 4. Build FormData
       * 5. POST to Worker
       * 6. Handle response
       */
      async submit() {
        this.error = '';

        // 1. Required field validation
        for (const key of requiredFields) {
          if (!this.form[key] || String(this.form[key]).trim() === '') {
            this.error = 'Please complete all required fields.';
            return;
          }
        }

        // 2. File size validation
        if (hasFiles && this.fileObjects.length > 0) {
          const totalSize = this.fileObjects.reduce((sum, f) => sum + f.size, 0);
          if (totalSize > window.FormHandler.MAX_UPLOAD_BYTES) {
            const maxMb = Math.round(window.FormHandler.MAX_UPLOAD_BYTES / 1024 / 1024);
            this.error = `Total file size exceeds the ${maxMb} MB limit. Please reduce file sizes and try again.`;
            return;
          }
        }

        // Check configuration
        const workerUrl = window.GJ?.formWorkerUrl;
        const siteKey = window.GJ?.turnstileSiteKey;

        if (!workerUrl) {
          this.error = 'Form service is not configured. Please contact us directly.';
          console.error('FormHandler: window.GJ.formWorkerUrl is not set.');
          return;
        }

        this.busy = true;

        try {
          // 3. Acquire Turnstile token
          let turnstileToken = '';
          if (siteKey && typeof turnstile !== 'undefined') {
            turnstileToken = await this._getTurnstileToken(siteKey);
          }

          // 4. Build FormData
          const fd = new FormData();
          fd.append('form_type', formType);

          if (turnstileToken) {
            fd.append('cf-turnstile-response', turnstileToken);
          }

          // Append all form fields
          Object.entries(this.form).forEach(([key, value]) => {
            fd.append(key, typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value));
          });

          // Append file objects
          if (hasFiles) {
            this.fileObjects.forEach((file) => {
              fd.append('files', file, file.name);
            });
          }

          // 5. POST to Worker
          const response = await fetch(workerUrl + '/submit', {
            method: 'POST',
            body: fd,
          });

          const result = await response.json();

          // 6. Handle response
          if (result.success) {
            this.success = true;
            this.refNumber = this._generateRef();
            if (typeof onSuccess === 'function') {
              onSuccess.call(this);
            }
          } else {
            this.error = result.error || 'Something went wrong. Please try again or contact us directly.';
          }
        } catch {
          this.error = 'Unable to send your message. Please check your connection and try again.';
        } finally {
          this.busy = false;
        }
      },

      /**
       * Acquire a Turnstile token using explicit rendering.
       * Renders the widget once per form, then executes it on each submit.
       *
       * @param {string} siteKey
       * @returns {Promise<string>}
       * @private
       */
      _getTurnstileToken(siteKey) {
        return new Promise((resolve, reject) => {
          try {
            /* global turnstile */
            const containerId = `turnstile-${formType}`;
            let container = document.getElementById(containerId);

            // Create container if it doesn't exist
            if (!container) {
              container = document.createElement('div');
              container.id = containerId;
              container.style.display = 'none';
              document.body.appendChild(container);
            }

            // Remove any existing widget before re-rendering
            if (container.dataset.widgetId) {
              try {
                turnstile.remove(container.dataset.widgetId);
              } catch {
                // Widget may already be gone
              }
              container.dataset.widgetId = '';
            }

            // Timeout — don't hang forever
            const timeout = setTimeout(() => {
              console.warn('FormHandler: Turnstile timed out, submitting without token.');
              resolve('');
            }, 10000);

            const widgetId = turnstile.render(`#${containerId}`, {
              sitekey: siteKey,
              size: 'invisible',
              callback: (token) => {
                clearTimeout(timeout);
                resolve(token);
              },
              'error-callback': () => {
                clearTimeout(timeout);
                reject(new Error('Turnstile challenge failed'));
              },
              'expired-callback': () => {
                clearTimeout(timeout);
                reject(new Error('Turnstile token expired'));
              },
            });

            container.dataset.widgetId = widgetId;
          } catch (err) {
            // Turnstile not loaded — degrade gracefully
            console.warn('FormHandler: Turnstile not available, submitting without token.', err);
            resolve('');
          }
        });
      },

      /**
       * Generate a client-side reference number for display.
       *
       * @returns {string}
       * @private
       */
      _generateRef() {
        return 'GJ-' + new Date().getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000);
      },
    };
  },
};
